<script>
import { mapActions } from 'vuex';

// Best Practice: Define constants outside the component definition.
const VUE_APP_SUBSCRIPTION_ALERT_KEY = 'subscriptionAlertShownTimestamp';
const ACTIVE_STATUS = ['active', 'trialing'];
export default {
  name: 'uConnectActivationAlert',
  data() {
    return {
      showSubscriptionAlert: false,
      subscriptionAlertTitle: '',
      subscriptionAlertMessage: '',
      subscriptionAlertType: 'info',
      contactEmail: "support@unifiproperty.com",
      privateMode: false,
      // The constant key is no longer needed in the reactive data object.
    };
  },
  methods: {
    ...mapActions(['getPermission']),

    /**
     * Checks the admin's subscription status using modern async/await.
     * This is much cleaner and easier to read than the old callback pattern.
     */
    async checkSubscriptionStatus() {
      const lastShownTimestamp = localStorage.getItem(VUE_APP_SUBSCRIPTION_ALERT_KEY);
      const oneDayInMs = 24 * 60 * 60 * 1000;
      const now = new Date().getTime();

      // Don't proceed if the user has already closed the alert in the last 24 hours.
      if (lastShownTimestamp && (now - lastShownTimestamp < oneDayInMs)) {
        return;
      }

      // This assumes you have a Vuex action that hits the 'oa/admin/permission' endpoint
      // and returns an object like { code: 0, data: { stripeSubscriptionStatus: 'past_due' } }
      this.getPermission({params: {}, callback: this.permissionCallback});

    },
    permissionCallback(res) {
      console.log("permissionCallback", res);
      if (res && res.code === 0 && res.data.stripeSubscriptionStatus !== '' && !ACTIVE_STATUS.includes(res.data.stripeSubscriptionStatus)) {
        const status = res.data.stripeSubscriptionStatus;
        // Reuse your existing helper to get formatted status info
        const statusInfo = this.getStatusInfo(status);

        this.subscriptionAlertTitle = `Subscription ${statusInfo.text}`;
        this.subscriptionAlertMessage = `Your subscription is currently ${statusInfo.text}. ${statusInfo.description} Please update your payment details to ensure uninterrupted service.`;
        this.subscriptionAlertType = statusInfo.type;
        this.showSubscriptionAlert = true;

        // Store the timestamp so we don't show it again for 24 hours.
        // const now = new Date().getTime();
        // localStorage.setItem(this.VUE_APP_SUBSCRIPTION_ALERT_KEY, now.toString());

      } else if (res && res.code === 0 && res.data.stripeSubscriptionStatus === 'active') {
        // If the subscription is active, clear the key so the banner can appear
        // immediately if the status lapses in the future.
        localStorage.removeItem(VUE_APP_SUBSCRIPTION_ALERT_KEY);
      }
    },

    /**
     * Handles the user closing the alert banner.
     */
    handleSubscriptionAlertClose() {
      this.showSubscriptionAlert = false;
      // Set the timestamp on close to respect the "once per day" rule.
      localStorage.setItem(VUE_APP_SUBSCRIPTION_ALERT_KEY, new Date().getTime().toString());
    },

    /**
     * Redirects the user to the Stripe billing portal.
     * Replace with your actual billing portal URL.
     */
    goToBilling() {
      // TODO: Replace with the actual URL to your Stripe customer billing portal
      // window.open('https://billing.stripe.com/p/login/xxxxxxxxxxxxxx', '_blank');
    },

    getStatusInfo(status) {
      if (!status) {
        return { type: 'info', text: 'Unknown', description: 'No subscription status available.' };
      }
      const statusKey = status.toLowerCase();
      const statusMap = {
        incomplete: { type: 'warning', text: 'Incomplete', description: 'Created but first invoice not paid yet.' },
        incomplete_expired: { type: 'danger', text: 'Expired', description: 'The first invoice wasn’t paid within 23 hours, subscription expired.' },
        trialing: { type: 'info', text: 'Trialing', description: 'Customer is in a trial period.' },
        active: { type: 'success', text: 'Active', description: 'Payments are up to date, subscription is running normally.' },
        past_due: { type: 'error', text: 'Overdue', description: 'Last invoice wasn’t paid, payment required.' },
        canceled: { type: 'info', text: 'Canceled', description: 'Subscription has been canceled.' },
        unpaid: { type: 'error', text: 'Unpaid', description: 'Payments failed, subscription is unpaid.' }
      };
      const result = statusMap[statusKey];
      if (result) {
        return result;
      }
      // Fallback for any other status, format it nicely
      const formattedText = status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      return { type: 'info', text: formattedText, description: `Status: ${formattedText}` };
    },
  },
  created() {
    if (this.$route.path.slice(0,9) === '/private/') {
      this.privateMode = true;
    }
  },
  mounted() {
    if (!this.privateMode) {
      this.checkSubscriptionStatus();
    }
  },
};
</script>

<template>
  <div>
    <el-alert
    v-if="showSubscriptionAlert"
    :title="subscriptionAlertTitle"
    :type="subscriptionAlertType"
    class="subscription-alert"
    show-icon
    :closable="true"
    @close="handleSubscriptionAlertClose"
    >
    <div class="alert-content">
      <p>{{ subscriptionAlertMessage }}</p>
      <div class="alert-actions">
<!--        <el-button type="primary" size="mini" @click="goToBilling">Pay Now</el-button>-->
        <span class="contact-us font400">Need assistance? Contact us at <a target="_blank" v-bind:href="'mailto:' + contactEmail" class="contact-us-email font400">{{contactEmail}}</a></span>
      </div>
    </div>
    </el-alert>

  </div>
</template>

<style scoped lang="scss">
.subscription-alert {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2000; /* A high z-index to ensure it's on top of other content */
  border-radius: 0; /* Remove rounded corners for a clean, full-width look */
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1); /* Add a subtle shadow to lift it off the page */
}
.subscription-alert ::v-deep .el-alert__content {
  width: 100%; /* let our content span full alert width */
}
.alert-content {
  display: flex;
  align-items: center;
  width: 100%;
}

.alert-content p {
  flex: 1;
  margin: 0;
  padding-right: 160px; /* reserve space for buttons */
}

.alert-actions {
  position: absolute;
  right: 50px;   /* stick to the right edge */
  top: 50%;
  transform: translateY(-50%); /* vertical center */
  display: flex;
  align-items: center;
}

</style>



// WEBPACK FOOTER //
// src/components/uConnect/alert/uconnectActivationAlert.vue